import { logEvent } from "@src/appV2/lib/analytics";
import { useDebounce } from "@src/appV2/lib/utils/useDebounce";
import { useRecentlyVisited } from "@src/appV2/lib/utils/useRecentlyVisited";
import { ADMIN_EVENTS } from "@src/appV2/redesign/assets/constants/firebaseEvents";
import { useSearchAgents } from "@src/appV2/User/api/useSearchAgents";
import { useSearchFacilities } from "@src/appV2/User/api/useSearchFacilities";
import { useGetWorkplaceUserAccounts } from "@src/appV2/User/api/useGetWorkplaceUserAccounts";
import { useCallback, useMemo, useState } from "react";

import { type AccountSwitcherUser, type UserDetails } from "./types";
import { useHistory } from "react-router-dom";
import { getLocation } from "@src/appV2/redesign/utils/routes";
import { getWorkerStageLabel, isWorkerStageEnum } from "@src/appV2/redesign/utils/worker";
import { useDispatch } from "react-redux";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { pseudo } from "@src/modules/session";

export const AGENTS_LIMIT_FOR_LOAD = 3;
export const FACILITIES_LIMIT_FOR_LOAD = 3;

function getStageLabel(stage: string): string {
  if (isWorkerStageEnum(stage)) {
    return getWorkerStageLabel(stage).toUpperCase();
  }

  return stage.toUpperCase();
}

const INACTIVE_STATUSES = new Set([
  // Worker inactive stages
  "PROBATION",
  "DEACTIVATED",
  "DELETION_REQUESTED",
  "SOFT_DELETED",
  "DELETED",
  // Facility inactive statuses
  "UNSIGNED",
  "SUSPENDED",
  "TERMINATED",
  "ARCHIVED",
  "PENDING",
]);

function isInactiveStatus(status: string): boolean {
  return INACTIVE_STATUSES.has(status.toUpperCase());
}

function sortByActiveStatus<T extends { stage?: string }>(list: T[]): T[] {
  return [...list].sort((a, b) => {
    const aInactive = a.stage ? isInactiveStatus(a.stage) : false;
    const bInactive = b.stage ? isInactiveStatus(b.stage) : false;

    if (aInactive && !bInactive) {
      return 1;
    }
    if (!aInactive && bInactive) {
      return -1;
    }
    return 0;
  });
}

export function useAccountSwitcher(): {
  switcherTitle: string;
  switcherSubtitle?: string;
  displayAdminSwitcher: boolean;
  dropdownVisible: boolean;
  setDropdownVisible: (visible: boolean) => void;
  onAdminSearchBarEntered: () => void;
  onDropdownButtonClicked: () => void;
  onDropdownMouseLeave: () => void;
  workerDetails: UserDetails;
  workplaceDetails: UserDetails;
  onSearchChanged: (name: string) => void;
  onUserClicked: (user: AccountSwitcherUser) => void;
  onAdminClicked: () => void;
  hasSearchTerm: boolean;
} {
  const session = useSession();
  const { profile, adminUserId, admin, adminProfile, type, user } = session;

  const facilityName = profile.name;
  const userName = user?.name;
  const adminName = adminProfile?.name;
  const isFacilityUser = type === "FACILITY";

  // Determine title and subtitle based on user type and context
  const { switcherTitle, switcherSubtitle } = (() => {
    if (admin) {
      return isFacilityUser && facilityName
        ? {
            // Admin viewing a facility
            switcherTitle: facilityName,
            switcherSubtitle: adminName ?? "Administrator",
          }
        : {
            // Admin not viewing a facility
            switcherTitle: adminName ?? "Select Account",
            switcherSubtitle: "Administrator",
          };
    }

    if (isFacilityUser) {
      return facilityName
        ? {
            // Non-admin facility user
            switcherTitle: facilityName,
            switcherSubtitle: userName,
          }
        : {
            // No facility selected (shouldn't happen for non-admin)
            switcherTitle: userName ?? "Facility",
            switcherSubtitle: undefined,
          };
    }

    // Fallback
    return {
      switcherTitle: "Select Account",
      switcherSubtitle: undefined,
    };
  })();

  const displayAdminSwitcher = admin;

  const [dropdownVisible, setDropdownVisible] = useState(false);

  const onAdminSearchBarEntered = useCallback(() => {
    logEvent(ADMIN_EVENTS.OPEN_SEARCH);
    setDropdownVisible(true);
  }, []);

  const onDropdownButtonClicked = useCallback(() => {
    setDropdownVisible((prev) => {
      if (!prev) {
        logEvent(ADMIN_EVENTS.OPEN_SEARCH);
      }
      return !prev;
    });
  }, []);

  const onDropdownMouseLeave = useCallback(() => {
    setDropdownVisible(false);
  }, []);

  const [searchName, setSearchName] = useState<string>("");

  const logSearchEvent = useCallback((value: string) => {
    if (value) {
      logEvent(ADMIN_EVENTS.FILTER_SEARCH, { user_input: value });
    }
  }, []);

  const debouncedSearchName = useDebounce({
    value: searchName,
    debounceTimeInMs: 800,
    onUpdateValue: logSearchEvent,
  });

  // Admin search functionality - only runs for admin users
  const {
    data: searchUsersResponse,
    isLoading: isSearchUsersLoading,
    fetchNextPage: fetchNextPageSearchUsers,
    fetchStatus: fetchStatusSearchUsers,
    isFetchingNextPage: isFetchingNextPageSearchUsers,
    hasNextPage: hasNextPageSearchUsers,
  } = useSearchAgents(
    {
      searchInput: debouncedSearchName,
      limit: AGENTS_LIMIT_FOR_LOAD,
    },
    { enabled: admin }
  );

  const workerDetails: UserDetails = useMemo(
    () => ({
      list: sortByActiveStatus(
        searchUsersResponse?.pages.flatMap((page) =>
          // FIXME: Automatically disabled during ESLint config update
          // eslint-disable-next-line @typescript-eslint/no-shadow
          page.list.map((user) => ({
            type: "AGENT",
            userId: user.userId,
            name: user.name,
            title: user.name,
            subtitles: [user.userId, `${user.phone} · ${user.email}`],
            stage: getStageLabel(user.stage),
          }))
        ) ?? []
      ),
      loading: isSearchUsersLoading && fetchStatusSearchUsers !== "idle",
      fetchNextPage: fetchNextPageSearchUsers,
      isFetchingNextPage: isFetchingNextPageSearchUsers,
      hasNextPage: hasNextPageSearchUsers,
    }),
    [
      searchUsersResponse,
      isSearchUsersLoading,
      fetchStatusSearchUsers,
      fetchNextPageSearchUsers,
      isFetchingNextPageSearchUsers,
      hasNextPageSearchUsers,
    ]
  );

  // Admin workplace search - only runs for admin users
  const {
    data: searchFacilitiesResponse,
    isLoading: isSearchFacilitiesLoading,
    fetchNextPage: fetchNextPageSearchFacilities,
    fetchStatus: fetchStatusSearchFacilities,
    isFetchingNextPage: isFetchingNextPageSearchFacilities,
    hasNextPage: hasNextPageSearchFacilities,
  } = useSearchFacilities(
    {
      searchInput: debouncedSearchName,
      limit: AGENTS_LIMIT_FOR_LOAD,
    },
    { enabled: admin }
  );

  const adminWorkplaceDetails: UserDetails = useMemo(
    () => ({
      list: sortByActiveStatus(
        searchFacilitiesResponse?.pages.flatMap((page) =>
          // FIXME: Automatically disabled during ESLint config update
          // eslint-disable-next-line @typescript-eslint/no-shadow
          page.list.map((user) => ({
            type: "WORKPLACE",
            userId: user.userId,
            name: user.name,
            title: user.name,
            subtitles: [user.userId, user.pricingMsa],
            stage: getStageLabel(user.status),
          }))
        ) ?? []
      ),
      loading: isSearchFacilitiesLoading && fetchStatusSearchFacilities !== "idle",
      fetchNextPage: fetchNextPageSearchFacilities,
      isFetchingNextPage: isFetchingNextPageSearchFacilities,
      hasNextPage: hasNextPageSearchFacilities,
    }),
    [
      searchFacilitiesResponse,
      isSearchFacilitiesLoading,
      fetchStatusSearchFacilities,
      fetchNextPageSearchFacilities,
      isFetchingNextPageSearchFacilities,
      hasNextPageSearchFacilities,
    ]
  );

  // Non-admin workplace user accounts - fetches workplaces associated with the current user
  const {
    data: workplaceUserAccountsResponse,
    isLoading: isWorkplaceUserAccountsLoading,
    isSuccess: isWorkplaceUserAccountsSuccess,
  } = useGetWorkplaceUserAccounts(
    { name: debouncedSearchName },
    { enabled: !admin && isFacilityUser }
  );

  const nonAdminWorkplaceDetails: UserDetails = useMemo(() => {
    return {
      list: isWorkplaceUserAccountsSuccess
        ? workplaceUserAccountsResponse.facilities.map((workplace) => ({
            type: "WORKPLACE" as const,
            userId: workplace.userId,
            name: workplace.name,
            title: workplace.name,
          }))
        : [],
      loading: isWorkplaceUserAccountsLoading,
    };
  }, [
    workplaceUserAccountsResponse,
    isWorkplaceUserAccountsLoading,
    isWorkplaceUserAccountsSuccess,
  ]);

  // Use the appropriate workplace details based on user type
  const workplaceDetails = admin ? adminWorkplaceDetails : nonAdminWorkplaceDetails;

  const onSearchChanged = useCallback((name: string) => {
    setSearchName(name);
  }, []);

  const [, addToRecent] = useRecentlyVisited();
  const history = useHistory();
  const dispatch = useDispatch();

  const handlePseudo = useCallback(
    (data: { userId: string }, isFirebaseSession?: boolean) => {
      return dispatch(pseudo(data, isFirebaseSession));
    },
    [dispatch]
  );

  // Admin user switching - requires page reload to fully update session
  const switchUserAsAdmin = useCallback(
    async (userId: string) => {
      setDropdownVisible(false);
      await handlePseudo({ userId });
      history.push(getLocation("enter"));
      window.location.reload();
    },
    [handlePseudo, history]
  );

  // Non-admin workplace user switching - simpler flow without reload
  const switchUserAsWorkplaceUser = useCallback(
    async (userId: string) => {
      setDropdownVisible(false);
      localStorage.setItem("selectedFacility", userId);
      await handlePseudo({ userId }, true);
      window.location.reload();
    },
    [handlePseudo]
  );

  const onUserClicked = useCallback(
    (accountUser: AccountSwitcherUser) => {
      const { userId, name, type: userType } = accountUser;

      addToRecent({ id: userId, title: name, type: userType });

      if (userType === "AGENT") {
        logEvent(ADMIN_EVENTS.SELECT_HCP, { id: userId });
      } else if (userType === "WORKPLACE") {
        logEvent(ADMIN_EVENTS.SELECT_HCF, { id: userId });
      }

      // Use different switching logic based on whether user is admin
      if (admin) {
        void switchUserAsAdmin(userId);
      } else {
        void switchUserAsWorkplaceUser(userId);
      }
    },
    [admin, addToRecent, switchUserAsAdmin, switchUserAsWorkplaceUser]
  );

  const onAdminClicked = useCallback(() => {
    if (adminUserId) {
      void switchUserAsAdmin(adminUserId);
    }
  }, [adminUserId, switchUserAsAdmin]);

  return {
    switcherTitle,
    switcherSubtitle,
    displayAdminSwitcher,
    dropdownVisible,
    setDropdownVisible,
    onAdminSearchBarEntered,
    onDropdownButtonClicked,
    onDropdownMouseLeave,
    workerDetails,
    workplaceDetails,
    onSearchChanged,
    onUserClicked,
    onAdminClicked,
    hasSearchTerm: Boolean(searchName && searchName.length > 0),
  };
}
